/**
 * User Service - API methods for user/member management
 */

import apiClient from './apiClient';

// List members request/response types
export interface ListMembersParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'active' | 'inactive';
  role?: string;
  organizationId?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// Create member request
export interface CreateMemberRequest {
  email: string;
  firstName: string;
  lastName: string;
  password?: string;
  roleIds?: string[];
  groupIds?: string[];
  organizationId?: string;
  primaryCenterId?: string;
  activateLogin?: boolean;
}

// Normalised create-member result (member + optional generated password)
export interface CreateMemberResponse extends Member {
  temporaryPassword?: string;
}

// Raw envelope from POST /members: { success, data: Member, temporaryPassword?, message }
interface CreateMemberApiResponse {
  success: boolean;
  data: Member;
  temporaryPassword?: string;
  message?: string;
}

// Bulk user validation row (mirrors API contract)
export interface BulkUserValidateRow {
  rowNumber: number;
  email?: string;
  firstName?: string;
  lastName?: string;
  roleName?: string;
  roleId?: string;
  activateLogin?: boolean;
  status: 'valid' | 'invalid';
  reason?: string;
}

// Normalised row shape used in the UI
export interface BulkUserRow {
  row: number;
  email?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  roleId?: string;
  activateLogin?: boolean;
  valid: boolean;
  reason?: string;
}

// Validate response
export interface BulkUserValidateResponse {
  rows: BulkUserRow[];
  validCount: number;
  invalidCount: number;
  totalCount: number;
}

// Credentials returned after bulk execute
export interface BulkUserCredential {
  email: string;
  firstName: string;
  lastName: string;
  temporaryPassword: string;
  // False when Entra provisioning failed — the password won't work to sign in
  // until it's retried (e.g. reset-password).
  entraProvisioned: boolean;
}

// Execute response
export interface BulkUserExecuteResponse {
  created: number;
  skipped: number;
  errors: Array<{ email: string; reason: string }>;
  credentials: BulkUserCredential[];
}

// Update member request
export interface UpdateMemberRequest {
  firstName?: string;
  lastName?: string;
  roleIds?: string[];
  groupIds?: string[];
  status?: 'active' | 'inactive';
  organizationId?: string;
  primaryCenterId?: string | null;
}

// Role object from API
export interface MemberRole {
  id: string;
  name: string;
  description?: string;
}

// Group object from API
export interface MemberGroup {
  id: string;
  name: string;
  description?: string;
}

// Member response from API
export interface Member {
  id: string;
  userId: string;
  organizationId: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  userStatus: string;
  loginActivated: boolean;
  supervisorId?: string;
  primaryCenterId?: string | null;
  primaryCenter?: { id: string; name: string } | null;
  roles?: MemberRole[];
  groups?: MemberGroup[];
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
  // Computed status for UI compatibility
  status: 'active' | 'inactive';
  // Fields returned by some endpoints (e.g. hierarchy/detail views)
  supervisorName?: string | null;
  assignedClients?: { id: string; name: string; location?: string }[];
  // Present on create when `activateLogin` was requested — false means Entra
  // provisioning failed and the surfaced `temporaryPassword` won't sign in yet.
  entraProvisioned?: boolean;
}

// Raw member response from API (before transformation)
interface RawMember {
  id: string;
  userId: string;
  organizationId: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  userStatus: string;
  loginActivated: boolean;
  supervisorId?: string;
  supervisorName?: string | null;
  primaryCenterId?: string | null;
  primaryCenter?: { id: string; name: string } | null;
  roles?: MemberRole[];
  groups?: MemberGroup[];
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
}

// Transform API response to include status field for UI
const transformMember = (raw: RawMember): Member => ({
  ...raw,
  status: raw.isActive ? 'active' : 'inactive',
});

/**
 * Get paginated list of organization members
 */
export const getMembers = async (
  params: ListMembersParams = {},
): Promise<PaginatedResponse<Member>> => {
  const response = await apiClient.get<{
    success: boolean;
    data: RawMember[];
    total: number;
    page: number;
    limit: number;
  }>('/api/v1/members', { params });
  return {
    data: response.data.data.map(transformMember),
    meta: {
      total: response.data.total,
      page: response.data.page,
      limit: response.data.limit,
      totalPages: Math.ceil(response.data.total / response.data.limit),
    },
  };
};

/**
 * Get all active members across all pages (for dropdowns / transfer wizards).
 * The members endpoint caps each page at 100; this paginates transparently.
 */
export const getAllActiveMembers = async (): Promise<Member[]> => {
  return getAllMembers({ status: 'active' });
};

/**
 * Fetch every member matching the given search/status filters across all pages.
 * Uses page size 100 to minimise round trips. Intended for the Members table
 * which needs the full set so client-side role filtering and cross-page
 * select-all work correctly.
 */
export const getAllMembers = async (
  params: Pick<ListMembersParams, 'search' | 'status'> = {},
): Promise<Member[]> => {
  const PAGE_SIZE = 100;
  const first = await getMembers({ ...params, limit: PAGE_SIZE, page: 1 });
  const all = [...first.data];
  if (first.meta.totalPages > 1) {
    const rest = await Promise.all(
      Array.from({ length: first.meta.totalPages - 1 }, (_, i) =>
        getMembers({ ...params, limit: PAGE_SIZE, page: i + 2 }),
      ),
    );
    rest.forEach((r) => all.push(...r.data));
  }
  return all;
};

/**
 * Get member by ID. Unwraps the { success, data } envelope and maps to the UI
 * `Member` shape (adds the computed `status`) like getMembers does.
 */
export const getMemberById = async (id: string): Promise<Member> => {
  const response = await apiClient.get<{ success: boolean; data: RawMember }>(
    `/api/v1/members/${id}`,
  );
  return transformMember(response.data.data);
};

/**
 * Create new organization member.
 * Omit `password` to let the server auto-generate a temporary one.
 * Returns the member data merged with `temporaryPassword` when the server generated one.
 */
export const createMember = async (data: CreateMemberRequest): Promise<CreateMemberResponse> => {
  const response = await apiClient.post<CreateMemberApiResponse>('/api/v1/members', data);
  const { data: member, temporaryPassword } = response.data;
  return { ...member, temporaryPassword };
};

/**
 * Validate a bulk user upload CSV file (two-phase flow, step 1).
 * Pass `organizationId` for super-admin requests (API reads it from query param).
 */
export const bulkValidateUsers = async (
  file: File,
  organizationId?: string,
): Promise<BulkUserValidateResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  const params = organizationId ? { organizationId } : undefined;
  const { data } = await apiClient.post<{
    success: boolean;
    data: {
      valid: BulkUserValidateRow[];
      invalid: BulkUserValidateRow[];
      validCount: number;
      invalidCount: number;
      totalRows: number;
    };
  }>('/api/v1/members/bulk/validate', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    params,
  });

  const normalise = (r: BulkUserValidateRow): BulkUserRow => ({
    row: r.rowNumber,
    email: r.email,
    firstName: r.firstName,
    lastName: r.lastName,
    role: r.roleName,
    roleId: r.roleId,
    activateLogin: r.activateLogin,
    valid: r.status === 'valid',
    reason: r.reason,
  });

  const rows = [...data.data.valid, ...data.data.invalid]
    .sort((a, b) => a.rowNumber - b.rowNumber)
    .map(normalise);

  return {
    rows,
    validCount: data.data.validCount,
    invalidCount: data.data.invalidCount,
    totalCount: data.data.totalRows,
  };
};

/**
 * Execute bulk user creation for pre-validated rows (two-phase flow, step 2).
 * Pass `organizationId` for super-admin requests (API reads it from query param).
 */
export const bulkExecuteUsers = async (
  rows: BulkUserRow[],
  organizationId?: string,
): Promise<BulkUserExecuteResponse> => {
  // Send `roleName` (not `role`) — API DTO uses forbidNonWhitelisted
  const payload = rows.map((r) => ({
    email: r.email ?? '',
    firstName: r.firstName ?? '',
    lastName: r.lastName ?? '',
    roleId: r.roleId,
    roleName: r.role,
    activateLogin: r.activateLogin,
  }));
  const params = organizationId ? { organizationId } : undefined;
  const { data } = await apiClient.post<{ success: boolean; data: BulkUserExecuteResponse }>(
    '/api/v1/members/bulk/execute',
    { rows: payload },
    { params },
  );
  return data.data;
};

/**
 * Update organization member
 */
export const updateMember = async (id: string, data: UpdateMemberRequest): Promise<Member> => {
  const { organizationId, ...body } = data;
  const params = organizationId ? { organizationId } : undefined;
  const response = await apiClient.patch<Member>(`/api/v1/members/${id}`, body, { params });
  return response.data;
};

/**
 * Deactivate organization member
 */
export const deactivateMember = async (id: string, organizationId?: string): Promise<Member> => {
  const params = organizationId ? { organizationId } : undefined;
  const response = await apiClient.post<Member>(`/api/v1/members/${id}/deactivate`, undefined, {
    params,
  });
  return response.data;
};

/**
 * Reactivate organization member
 */
export const reactivateMember = async (id: string, organizationId?: string): Promise<Member> => {
  const params = organizationId ? { organizationId } : undefined;
  const response = await apiClient.post<Member>(`/api/v1/members/${id}/reactivate`, undefined, {
    params,
  });
  return response.data;
};

/**
 * Delete organization member (soft delete)
 */
export const deleteMember = async (id: string): Promise<void> => {
  await apiClient.delete(`/api/v1/members/${id}`);
};

const userService = {
  getMembers,
  getAllActiveMembers,
  getAllMembers,
  getMemberById,
  createMember,
  bulkValidateUsers,
  bulkExecuteUsers,
  updateMember,
  deactivateMember,
  reactivateMember,
  deleteMember,
};

export default userService;
